import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@/components/base/Alert';
import Button from '@/components/base/Button';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Modal from '@/components/base/Modal';
import Skeleton from '@/components/base/Skeleton';
import { cn } from '@/lib/cn';
import { fetchAvailableDrivers } from '@/services/driversService';
import type { Driver } from '@/types';

interface AssignDriverModalProps {
  open: boolean;
  onClose: () => void;
  onAssign: (driver: Driver) => Promise<void>;
}

export default function AssignDriverModal({ open, onClose, onAssign }: AssignDriverModalProps) {
  const { t } = useTranslation();

  const [drivers, setDrivers] = useState<Driver[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchAvailableDrivers();
      setDrivers(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setActionError(null);
    setSaving(false);
    load();
  }, [open, load]);

  const handleConfirm = async () => {
    const driver = drivers?.find((d) => d.id === selectedId);
    if (!driver) return;
    setSaving(true);
    setActionError(null);
    try {
      await onAssign(driver);
      onClose();
    } catch (err) {
      const unavailable = err instanceof Error && err.message === 'task-unavailable';
      setActionError(unavailable ? t('orders.assign.unavailable') : t('orders.errorAction'));
      if (unavailable) load();
    } finally {
      setSaving(false);
    }
  };

  const empty = !loading && !error && drivers !== null && drivers.length === 0;

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      size="md"
      fullScreen
      title={t('orders.assign.title')}
      description={t('orders.assign.available')}
      footer={
        <>
          <Button variant="outline" size="md" onClick={onClose} disabled={saving}>
            {t('orders.confirm.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={saving}
            disabled={!selectedId || loading || error || empty}
            onClick={handleConfirm}
          >
            {t('orders.assign.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-100/60 px-3.5 py-3 text-xs leading-relaxed text-secondary-900">
          <i className="ri-information-line mt-0.5 text-base text-secondary-700" aria-hidden="true" />
          {t('orders.assign.note')}
        </p>

        {actionError ? <Alert variant="error">{actionError}</Alert> : null}

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 rounded-md border border-background-200 p-3.5">
                <Skeleton className="h-11 w-11 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <ErrorState
            compact
            title={t('orders.assign.error')}
            description={t('common.errorText')}
            retryLabel={t('common.retry')}
            onRetry={load}
          />
        ) : empty ? (
          <EmptyState
            icon="ri-user-unfollow-line"
            title={t('orders.assign.empty')}
            description={t('orders.assign.emptyHint')}
            action={
              <Button variant="outline" size="sm" icon="ri-refresh-line" onClick={load}>
                {t('orders.assign.refresh')}
              </Button>
            }
          />
        ) : (
          <ul className="space-y-2.5" role="radiogroup" aria-label={t('orders.assign.available')}>
            {(drivers ?? []).map((driver) => {
              const active = driver.id === selectedId;
              return (
                <li key={driver.id}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSelectedId(driver.id)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md border px-3.5 py-3 text-start transition-colors cursor-pointer',
                      active
                        ? 'border-primary-400 bg-primary-100/50 ring-1 ring-primary-300'
                        : 'border-background-200 bg-background-50 hover:bg-background-100',
                    )}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-500 text-sm font-bold text-background-50">
                      {driver.avatarUrl ? (
                        <img
                          src={driver.avatarUrl}
                          alt={driver.fullName}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        driver.fullName.charAt(0)
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground-950">
                        {driver.fullName}
                      </span>
                      <span className="lb-ltr-field mt-0.5 block text-xs text-foreground-500">
                        {driver.phone}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                        active ? 'border-primary-500 bg-primary-500 text-background-50' : 'border-background-300',
                      )}
                      aria-hidden="true"
                    >
                      {active ? <i className="ri-check-line text-xs leading-none" /> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}